// "Achterstallig (N)": alles wat over de datum is, standaard ingeklapt, per
// project gegroepeerd (project één keer als kop). Elke regel heeft dezelfde drie
// acties: naar vandaag / nieuwe datum / afgerond. Rood alleen voor de datum.
import React from 'react';
import { Box, Typography, Button, Collapse, Tooltip } from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useDraggable } from '@dnd-kit/core';
import { KLEUR, MAAT, ProjectTag, korteDatum } from './DagTaakRij';
import { groepeerOpProject, korteTitel } from './dagplanning';

const actieSx = {
  textTransform: 'none', minWidth: 0, px: 0.75, py: 0, height: 22, fontSize: MAAT.klein, fontWeight: 500,
  color: KLEUR.zacht, borderRadius: '6px', '&:hover': { backgroundColor: KLEUR.primairVlak, color: KLEUR.primair },
};

function Regel({ rij, onVandaag, onNieuweDatum, onAfgerond, onOpen, sleep }) {
  const titel = korteTitel(rij.titel, rij.projectName);
  const kanWijzigen = rij.soort !== 'deliverable';
  return (
    <Box {...(sleep || {})} sx={{
      display: 'flex', alignItems: 'center', gap: 1, py: 0.5, pl: 1, pr: 0.5, borderRadius: '8px',
      cursor: sleep ? 'grab' : 'default', '&:hover': { backgroundColor: KLEUR.achter },
    }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          onClick={(e) => onOpen?.(rij, e)}
          title={rij.titel}
          sx={{ fontSize: MAAT.tekst, fontWeight: 500, color: KLEUR.tekst, cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', '&:hover': { color: KLEUR.primair } }}
        >
          {titel}
        </Typography>
        <Typography sx={{ fontSize: MAAT.klein, color: KLEUR.zacht, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <Box component="span" sx={{ color: KLEUR.telaat }}>{korteDatum(rij.datum)}</Box>
          {rij.soort === 'deliverable' ? ' · deliverable' : ''}
          {rij.wie ? ` · ${rij.wie}` : rij.soort === 'eigen' ? ' · jij' : ''}
        </Typography>
      </Box>
      {kanWijzigen ? (
        <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }} onPointerDown={(e) => e.stopPropagation()}>
          <Tooltip describeChild title={rij.soort === 'eigen' ? 'Zet op je dag van vandaag' : 'Nieuwe einddatum: vandaag'}>
            <Button size="small" onClick={() => onVandaag(rij)} sx={actieSx}>Vandaag</Button>
          </Tooltip>
          <Button size="small" onClick={(e) => onNieuweDatum(rij, e.currentTarget)} sx={actieSx}>Datum</Button>
          <Button size="small" onClick={() => onAfgerond(rij)} sx={actieSx}>Afgerond</Button>
        </Box>
      ) : (
        <Button size="small" onClick={(e) => onOpen?.(rij, e)} sx={actieSx}>Bekijk</Button>
      )}
    </Box>
  );
}

function SleepbareRegel(props) {
  const { rij } = props;
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `achterstallig:${rij.key}`, data: { item: rij.taak, bron: 'achterstallig' },
  });
  return (
    <Box ref={setNodeRef} sx={{ opacity: isDragging ? 0.35 : 1, touchAction: 'none' }}>
      <Regel {...props} sleep={{ ...attributes, ...listeners }} />
    </Box>
  );
}

/**
 * rijen: [{ key, soort: 'eigen'|'pl'|'deliverable', titel, projectName, datum, wie, taak?, dl? }]
 */
export default function Achterstallig({
  rijen, open, onToggleOpen, onVandaag, onNieuweDatum, onAfgerond, onOpen,
}) {
  // De schakelaar voor interne projecten staat in Vandaag.jsx, onder deze sectie: hij
  // geldt ook voor de deadlines van vandaag, dus hoort niet alleen hierbinnen.
  if (!rijen.length) return null;
  const groepen = groepeerOpProject(rijen);
  return (
    <Box sx={{ mt: 0.5 }}>
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={onToggleOpen}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggleOpen(); } }}
        sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.5, px: 0.5, cursor: 'pointer', borderRadius: '8px', '&:hover': { backgroundColor: KLEUR.achter } }}
      >
        <Typography sx={{ fontSize: MAAT.tekst, color: KLEUR.zacht }}>
          Achterstallig ({rijen.length})
        </Typography>
        {open ? <ExpandMoreIcon sx={{ fontSize: 16, color: KLEUR.stil }} /> : <ChevronRightIcon sx={{ fontSize: 16, color: KLEUR.stil }} />}
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ maxHeight: 420, overflowY: 'auto', mt: 0.5 }}>
          {groepen.map((g) => (
            <Box key={g.project || '-'} sx={{ mb: 1 }}>
              <Box sx={{ px: 1, py: 0.25 }}>
                {g.project ? <ProjectTag naam={g.project} maxWidth={360} /> : (
                  <Typography sx={{ fontSize: MAAT.klein, color: KLEUR.zacht }}>Zonder project</Typography>
                )}
              </Box>
              {g.items.map((rij) => (rij.soort === 'eigen'
                ? <SleepbareRegel key={rij.key} rij={rij} onVandaag={onVandaag} onNieuweDatum={onNieuweDatum} onAfgerond={onAfgerond} onOpen={onOpen} />
                : <Regel key={rij.key} rij={rij} onVandaag={onVandaag} onNieuweDatum={onNieuweDatum} onAfgerond={onAfgerond} onOpen={onOpen} />))}
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}
